import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  Input,
  Inspector,
  Page,
  QueryState,
  Select,
  Stack,
  Table,
  Timeline,
  type Column,
  type InspectorField,
  type TimelineEntry,
  type Tone,
} from '@integra/ui';
import type { Publication } from '../api/types.js';
import {
  useAsset,
  useAssetVersions,
  useChangeAssetState,
  useBroadcastAsset,
  useRepurposePlan,
  useBrandSafetyCheck,
  useCompatibilityReport,
  useGenerateCopy,
  useDestinations,
  usePublications,
  usePublishAsset,
} from '../api/hooks.js';

/**
 * A single asset: its data, its lifecycle, its version history and where it has
 * actually landed (§6.1, §6.2).
 */

/** Provenance + delivery detail for one publication — the shared object drawer. */
function publicationInspectorFields(pub: Publication): InspectorField[] {
  const fields: InspectorField[] = [
    { label: 'State', value: pub.state },
    { label: 'Destination', value: <span className="font-mono text-xs">{pub.destinationId}</span> },
    {
      label: 'External id',
      value:
        pub.externalId === null ? (
          'None — the channel never acknowledged an object'
        ) : (
          <span className="font-mono text-xs">{pub.externalId}</span>
        ),
    },
  ];
  if (pub.externalUrl !== null) {
    fields.push({
      label: 'Live URL',
      value: (
        <a
          href={pub.externalUrl}
          target="_blank"
          rel="noreferrer"
          className="text-gold-text underline"
        >
          View on channel
        </a>
      ),
    });
  }
  fields.push({
    label: 'Last published',
    value: pub.lastPublishedAt === null ? 'Never' : new Date(pub.lastPublishedAt).toLocaleString(),
  });
  if (pub.lastError !== null) fields.push({ label: 'Last error', value: pub.lastError });
  fields.push({
    label: 'Publication id',
    value: <span className="font-mono text-xs">{pub.id}</span>,
  });
  return fields;
}

/**
 * The lifecycle graph (§6.2). Archived is terminal. Offering a transition the
 * server would reject is a lie the UI tells about what is possible, so only
 * legal moves are rendered at all.
 */
const LEGAL_TRANSITIONS: Record<string, readonly string[]> = {
  draft: ['active', 'archived'],
  active: ['delisted', 'archived'],
  delisted: ['active', 'archived'],
  archived: [],
};

const PUBLICATION_TONE: Record<string, Tone> = {
  live: 'success',
  failed: 'error',
  pending: 'warning',
  removed: 'neutral',
};

const VERSION_TONE: Record<string, Tone> = {
  draft: 'neutral',
  active: 'success',
  delisted: 'warning',
  archived: 'neutral',
};

export function AssetDetailPage() {
  const { workspaceId = '', assetId = '' } = useParams<{
    workspaceId: string;
    assetId: string;
  }>();

  const asset = useAsset(workspaceId, assetId);
  const versions = useAssetVersions(workspaceId, assetId);
  const publications = usePublications(workspaceId, assetId);
  const [inspectingPub, setInspectingPub] = useState<Publication | null>(null);
  const destinations = useDestinations(workspaceId);
  const changeState = useChangeAssetState(workspaceId, assetId);
  const publish = usePublishAsset(workspaceId, assetId);
  const broadcast = useBroadcastAsset(workspaceId, assetId);
  const repurpose = useRepurposePlan(workspaceId, assetId);
  const brandSafety = useBrandSafetyCheck(workspaceId, assetId);
  const compatibility = useCompatibilityReport(workspaceId, assetId);
  const copyGen = useGenerateCopy(workspaceId, assetId);

  const [destinationId, setDestinationId] = useState('');
  const [audience, setAudience] = useState('');
  // Delisting cascades irreversibly across every carrying Destination (§2.3),
  // so it is confirmed rather than fired on a single click.
  const [confirmDelist, setConfirmDelist] = useState(false);

  const current = asset.data;
  const transitions = current === undefined ? [] : (LEGAL_TRANSITIONS[current.state] ?? []);

  // Newest first: the most recent version is what the operator is reasoning about.
  const timeline: TimelineEntry[] = [...(versions.data?.data ?? [])]
    .sort((a, b) => b.version - a.version)
    .map((version) => ({
      id: `v${String(version.version)}`,
      title: `Version ${String(version.version)}`,
      detail: version.state,
      timestamp: version.createdAt,
      tone: VERSION_TONE[version.state] ?? 'neutral',
    }));

  const destinationList = destinations.data?.data ?? [];

  const publicationColumns: readonly Column<Publication>[] = [
    {
      key: 'destination',
      header: 'Destination',
      sortValue: (row) => row.destinationId,
      render: (row) => <span className="font-mono text-xs text-ink">{row.destinationId}</span>,
    },
    {
      key: 'state',
      header: 'State',
      sortValue: (row) => row.state,
      render: (row) => (
        <Badge tone={PUBLICATION_TONE[row.state] ?? 'neutral'} dot>
          {row.state}
        </Badge>
      ),
    },
    {
      key: 'externalId',
      header: 'External ID',
      render: (row) =>
        row.externalId === null ? (
          // No external id means the channel never acknowledged an object.
          <span className="text-xs text-ink-muted">none</span>
        ) : (
          <span className="font-mono text-xs text-ink">{row.externalId}</span>
        ),
    },
    {
      key: 'lastError',
      header: 'Last error',
      render: (row) =>
        row.lastError === null ? (
          <span className="text-xs text-ink-muted">—</span>
        ) : (
          <span className="text-xs text-state-error">{row.lastError}</span>
        ),
    },
    {
      key: 'inspect',
      header: '',
      align: 'right',
      render: (row) => (
        <button
          type="button"
          onClick={() => setInspectingPub(row)}
          className="text-sm text-ink-muted hover:text-navy-700 hover:underline"
        >
          Inspect
        </button>
      ),
    },
  ];

  return (
    <Page
      title={current === undefined ? 'Asset' : `${current.type} · v${String(current.version)}`}
      description="What this asset is, where it stands in its lifecycle, and every channel it has reached."
      actions={
        <Link
          to={`/w/${workspaceId}/assets`}
          className="text-sm font-semibold text-navy-700 hover:underline"
        >
          Back to assets
        </Link>
      }
    >
      <QueryState loading={asset.isLoading} error={asset.error} what="this asset">
        {current !== undefined && (
          <Stack gap="lg">
            <Card
              title="Lifecycle"
              subtitle={`Currently ${current.state}`}
              actions={
                <Badge tone={VERSION_TONE[current.state] ?? 'neutral'} dot>
                  {current.state}
                </Badge>
              }
            >
              <Stack gap="md">
                {transitions.length === 0 ? (
                  <p className="text-sm text-ink-muted">
                    Archived is terminal. There is no transition out of it.
                  </p>
                ) : (
                  <Cluster gap="sm">
                    {transitions.map((next) => (
                      <Button
                        key={next}
                        size="sm"
                        variant={
                          next === 'delisted' || next === 'archived' ? 'secondary' : 'primary'
                        }
                        loading={changeState.isPending}
                        onClick={() => {
                          if (next === 'delisted' && !confirmDelist) {
                            setConfirmDelist(true);
                            return;
                          }
                          setConfirmDelist(false);
                          changeState.mutate(next);
                        }}
                      >
                        Move to {next}
                      </Button>
                    ))}
                  </Cluster>
                )}

                {confirmDelist && (
                  <Alert tone="warning" title="Delisting cascades">
                    <Stack gap="sm">
                      <p>
                        Delisting does not only change this asset&apos;s state. It CASCADES: the
                        asset is removed from every Destination currently carrying it, on every
                        connected channel. Live listings will disappear.
                      </p>
                      <Cluster gap="sm">
                        <Button
                          size="sm"
                          variant="danger"
                          loading={changeState.isPending}
                          onClick={() => {
                            setConfirmDelist(false);
                            changeState.mutate('delisted');
                          }}
                        >
                          Delist and remove everywhere
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelist(false)}>
                          Cancel
                        </Button>
                      </Cluster>
                    </Stack>
                  </Alert>
                )}

                {changeState.error instanceof Error && (
                  <Alert tone="error" title="Transition rejected">
                    {changeState.error.message}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card title="Data" subtitle="The asset record exactly as the API returns it.">
              <pre className="overflow-x-auto font-mono text-xs text-ink">
                {JSON.stringify(current.data, null, 2)}
              </pre>
            </Card>

            <Card title="Version history" subtitle="Newest first.">
              <QueryState
                loading={versions.isLoading}
                error={versions.error}
                what="version history"
              >
                {timeline.length === 0 ? (
                  <EmptyState title="No versions recorded" />
                ) : (
                  <Timeline entries={timeline} />
                )}
              </QueryState>
            </Card>

            <Card
              title="Publish"
              subtitle="Send this asset to one channel, or to every connected channel at once."
            >
              <Stack gap="md">
                <QueryState
                  loading={destinations.isLoading}
                  error={destinations.error}
                  what="destinations"
                >
                  {destinationList.length === 0 ? (
                    <EmptyState
                      title="No destinations configured"
                      description="Add a connector and a destination before publishing."
                      action={
                        <Link
                          to={`/w/${workspaceId}/connectors`}
                          className="text-sm font-semibold text-navy-700 hover:underline"
                        >
                          Go to connectors
                        </Link>
                      }
                    />
                  ) : (
                    <Stack gap="md">
                      {/* Auto-repurpose + broadcast: one hero, adapted per channel,
                          published everywhere in one action (§9, §2.3). */}
                      <div className="rounded-lg border border-gold-400 bg-gold-200/40 p-4">
                        <Cluster justify="between" align="center" gap="md">
                          <div>
                            <p className="font-heading text-base text-navy-700">
                              Publish to all channels
                            </p>
                            <p className="mt-0.5 text-xs text-ink-muted">
                              This one asset is auto-adapted to each channel&apos;s limits, then
                              published to every active channel at once. Each delivery is verified
                              independently; one channel failing never stops the rest.
                            </p>
                          </div>
                          <Cluster gap="sm">
                            <Button
                              variant="ghost"
                              loading={compatibility.isPending}
                              onClick={() => compatibility.mutate(undefined)}
                            >
                              Check fit
                            </Button>
                            <Button
                              variant="ghost"
                              loading={brandSafety.isPending}
                              onClick={() => brandSafety.mutate()}
                            >
                              Check content
                            </Button>
                            <Button
                              variant="secondary"
                              loading={repurpose.isPending}
                              onClick={() => repurpose.mutate(undefined)}
                            >
                              Preview adaptation
                            </Button>
                            <Button
                              loading={broadcast.isPending}
                              onClick={() => broadcast.mutate(undefined)}
                            >
                              Publish everywhere
                            </Button>
                          </Cluster>
                        </Cluster>
                      </div>

                      {/* AI copywriting (§9, §21): distinct, audience-tailored copy
                          per channel — the step up from fitting one hero to limits.
                          Reviewed by a person, disclosed as AI-authored, never
                          auto-published. */}
                      <div className="rounded-lg border border-navy-200 bg-cream p-4">
                        <Cluster justify="between" align="end" gap="md">
                          <Field
                            label="Write channel copy for an audience (AI)"
                            htmlFor="copy-audience"
                          >
                            <Input
                              id="copy-audience"
                              value={audience}
                              placeholder="e.g. budget-conscious Gen Z, or leave blank"
                              onChange={(e) => setAudience(e.target.value)}
                            />
                          </Field>
                          <Button
                            variant="secondary"
                            loading={copyGen.isPending}
                            onClick={() =>
                              copyGen.mutate(
                                audience.trim() === '' ? {} : { audience: audience.trim() },
                              )
                            }
                          >
                            Write copy
                          </Button>
                        </Cluster>
                        <p className="mt-2 text-xs text-ink-muted">
                          Generates distinct, platform-native copy for each active channel, tuned to
                          the audience. AI-authored and disclosed &mdash; review and edit before you
                          publish.
                        </p>
                      </div>

                      <Cluster gap="md" align="end">
                        <Field label="Or publish to one destination" htmlFor="publish-destination">
                          <Select
                            id="publish-destination"
                            value={destinationId}
                            onChange={(e) => setDestinationId(e.target.value)}
                          >
                            <option value="">Choose a destination…</option>
                            {destinationList.map((destination) => (
                              <option key={destination.id} value={destination.id}>
                                {destination.id} ({destination.status})
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Button
                          variant="secondary"
                          loading={publish.isPending}
                          disabled={destinationId === ''}
                          onClick={() => publish.mutate(destinationId)}
                        >
                          Publish
                        </Button>
                      </Cluster>
                    </Stack>
                  )}
                </QueryState>

                {compatibility.data !== undefined && (
                  <Alert
                    tone={compatibility.data.blocked > 0 ? 'warning' : 'success'}
                    title={`Fit check: ${String(compatibility.data.ready)} ready, ${String(compatibility.data.adapts)} auto-adapt, ${String(compatibility.data.blocked)} blocked`}
                  >
                    <Stack gap="sm">
                      {compatibility.data.channels.map((ch) => (
                        <div
                          key={ch.destinationId}
                          className="border-b border-edge pb-2 last:border-0 last:pb-0"
                        >
                          <Cluster justify="between" align="center" gap="sm">
                            <span className="text-sm text-navy-700">{ch.channelName}</span>
                            <Badge
                              tone={
                                ch.status === 'blocked'
                                  ? 'error'
                                  : ch.status === 'adapts'
                                    ? 'warning'
                                    : 'success'
                              }
                              dot
                            >
                              {ch.status === 'ready'
                                ? 'lands as-is'
                                : ch.status === 'adapts'
                                  ? 'auto-adapts'
                                  : 'blocked'}
                            </Badge>
                          </Cluster>
                          {ch.findings.length > 0 && (
                            <ul className="mt-1 ml-4 list-disc space-y-0.5 text-xs text-ink-muted">
                              {ch.findings.map((f, i) => (
                                <li key={i}>
                                  <span
                                    className={
                                      f.kind === 'blocks' ? 'text-state-error' : 'text-ink-muted'
                                    }
                                  >
                                    {f.kind === 'blocks' ? 'Blocked' : 'Adapts'}:
                                  </span>{' '}
                                  {f.detail}
                                </li>
                              ))}
                            </ul>
                          )}
                          {ch.citation !== null && (
                            <p className="mt-1 text-[10px] text-ink-muted">
                              Judged against{' '}
                              <a
                                href={ch.citation.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-gold-text underline"
                              >
                                {ch.connectorKey} spec
                              </a>{' '}
                              (verified {ch.citation.lastVerified})
                            </p>
                          )}
                        </div>
                      ))}
                    </Stack>
                  </Alert>
                )}
                {compatibility.error instanceof Error && (
                  <Alert tone="error" title="Could not check fit">
                    {compatibility.error.message}
                  </Alert>
                )}

                {broadcast.data !== undefined && (
                  <Alert
                    tone={broadcast.data.failed === 0 ? 'success' : 'warning'}
                    title={`Broadcast: ${String(broadcast.data.live)} live, ${String(broadcast.data.pending)} in flight, ${String(broadcast.data.failed)} failed`}
                  >
                    <Stack gap="sm">
                      {broadcast.data.results.map((r) => (
                        <div
                          key={r.destinationId}
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                        >
                          <Badge
                            tone={
                              r.verified ? 'success' : r.state === 'failed' ? 'error' : 'warning'
                            }
                            dot
                          >
                            {r.verified ? 'live' : r.state}
                          </Badge>
                          <span className="font-semibold text-navy-700">{r.channelName}</span>
                          <span className="min-w-0 break-words text-ink-muted">
                            {r.error ??
                              (r.verified
                                ? 'confirmed live by the channel'
                                : 'accepted, awaiting confirmation')}
                          </span>
                        </div>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {broadcast.error instanceof Error && (
                  <Alert tone="error" title="Broadcast failed">
                    {broadcast.error.message}
                  </Alert>
                )}

                {repurpose.data !== undefined && (
                  <Alert
                    tone="info"
                    title={`Per-channel adaptation preview (${String(repurpose.data.channels.length)} channels)`}
                  >
                    <Stack gap="sm">
                      {repurpose.data.channels.map((ch) => (
                        <div key={ch.destinationId} className="text-xs">
                          <Cluster gap="sm" align="baseline">
                            <span className="font-semibold text-navy-700">{ch.channelName}</span>
                            <span className="text-ink-muted">
                              {ch.targetDimension === null
                                ? 'no fixed size'
                                : `${String(ch.targetDimension.width)}×${String(ch.targetDimension.height)}`}
                              {ch.targetFileType === null ? '' : ` · ${ch.targetFileType}`}
                            </span>
                          </Cluster>
                          {ch.adaptations.length === 0 ? (
                            <p className="text-ink-muted">Fits as-is, no changes needed.</p>
                          ) : (
                            <ul className="mt-0.5 space-y-0.5">
                              {ch.adaptations.map((a) => (
                                <li key={a.field} className="text-ink-muted">
                                  <span className="font-medium text-navy-700">{a.field}</span>:{' '}
                                  {a.reason} → &ldquo;{a.adapted}&rdquo;
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {repurpose.error instanceof Error && (
                  <Alert tone="error" title="Could not build the preview">
                    {repurpose.error.message}
                  </Alert>
                )}

                {brandSafety.data !== undefined && (
                  <Alert
                    tone={
                      !brandSafety.data.ok
                        ? 'error'
                        : brandSafety.data.concerns.length === 0
                          ? 'success'
                          : 'warning'
                    }
                    title={
                      brandSafety.data.concerns.length === 0
                        ? 'Brand-safety check: clear'
                        : `Brand-safety check: ${String(brandSafety.data.concerns.length)} to review`
                    }
                  >
                    {brandSafety.data.concerns.length === 0 ? (
                      <p className="text-xs">
                        No unfinished content or flagged terms. Safe to publish.
                      </p>
                    ) : (
                      <Stack gap="sm">
                        {brandSafety.data.concerns.map((concern, i) => (
                          <div
                            key={`${concern.field}-${concern.term}-${String(i)}`}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                          >
                            <Badge tone={concern.severity === 'block' ? 'error' : 'warning'} dot>
                              {concern.severity}
                            </Badge>
                            <span className="font-semibold text-navy-700">{concern.field}</span>
                            <span className="min-w-0 break-words text-ink-muted">
                              {concern.detail}
                            </span>
                          </div>
                        ))}
                      </Stack>
                    )}
                  </Alert>
                )}

                {brandSafety.error instanceof Error && (
                  <Alert tone="error" title="Could not check content">
                    {brandSafety.error.message}
                  </Alert>
                )}

                {copyGen.data !== undefined && (
                  <Alert
                    tone="info"
                    title={`AI copy for ${String(copyGen.data.variants.length)} channel${copyGen.data.variants.length === 1 ? '' : 's'}${copyGen.data.audience === null ? '' : ` · ${copyGen.data.audience}`}`}
                  >
                    <Stack gap="md">
                      <p className="text-xs text-ink-muted">{copyGen.data.disclosure}</p>
                      {copyGen.data.variants.map((v) => (
                        <div
                          key={v.destinationId}
                          className="rounded-md border border-edge bg-surface p-3"
                        >
                          <Cluster justify="between" align="center" gap="sm">
                            <span className="font-heading text-sm text-navy-700">
                              {v.channelName}
                            </span>
                            <Badge tone={v.withinLimit ? 'success' : 'warning'} dot>
                              {v.withinLimit
                                ? 'Within limit'
                                : `${String(v.bodyLength)}/${String(v.characterLimit ?? 0)} over`}
                            </Badge>
                          </Cluster>
                          {v.headline !== '' && (
                            <p className="mt-1.5 font-heading text-sm text-navy-700">
                              {v.headline}
                            </p>
                          )}
                          <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{v.body}</p>
                          {v.callToAction !== '' && (
                            <p className="mt-1.5 text-sm font-medium text-gold-text">
                              {v.callToAction}
                            </p>
                          )}
                          <p className="mt-1.5 text-xs italic text-ink-muted">{v.rationale}</p>
                        </div>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {copyGen.error instanceof Error && (
                  <Alert tone="error" title="Could not write copy">
                    {copyGen.error.message}
                  </Alert>
                )}

                {publish.data !== undefined &&
                  // The `verified` flag is the difference between "the channel
                  // confirmed this is live" and "the channel accepted the call".
                  // Reporting the second as the first is the false-green §2.7
                  // forbids.
                  (publish.data.verified ? (
                    <Alert tone="success" title="Published and verified live">
                      The destination confirmed the object is live
                      {publish.data.publication.externalId === null
                        ? '.'
                        : ` as ${publish.data.publication.externalId}.`}
                    </Alert>
                  ) : (
                    <Alert tone="warning" title="Accepted but not yet confirmed live">
                      The destination accepted the request, but INTEGRA has not verified the object
                      is live. Treat it as in flight, not published.
                    </Alert>
                  ))}

                {publish.error instanceof Error && (
                  <Alert tone="error" title="Publish failed">
                    {publish.error.message}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card
              title="Publications"
              subtitle="Where this asset actually stands, channel by channel."
            >
              <QueryState
                loading={publications.isLoading}
                error={publications.error}
                what="publications"
              >
                <Table
                  columns={publicationColumns}
                  rows={publications.data?.data ?? []}
                  rowKey={(row) => row.id}
                  empty={
                    <EmptyState
                      title="Not published anywhere yet"
                      description="This asset has never been sent to a destination."
                    />
                  }
                />
              </QueryState>
            </Card>
          </Stack>
        )}
      </QueryState>

      <Inspector
        open={inspectingPub !== null}
        title="Publication"
        {...(inspectingPub !== null ? { subtitle: 'Where this asset landed · provenance' } : {})}
        onClose={() => setInspectingPub(null)}
        fields={inspectingPub !== null ? publicationInspectorFields(inspectingPub) : []}
      />
    </Page>
  );
}
